import React, { useState, useEffect } from 'react';
import { X, Play, Mail, Gift, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POPUP_STORAGE_KEY = 'homelyph_video_popup_dismissed';

const VideoEmailCapture = ({ onClose, triggerType = 'timed' }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const isExitIntent = triggerType === 'exit-intent';

  // Track impression when popup is shown
  useEffect(() => {
    const trackImpression = async () => {
      try {
        await axios.post(`${API_URL}/api/popup/track?event_type=impression&trigger_type=${triggerType}&page=video-gallery`);
      } catch (e) {
        // Silently fail - analytics shouldn't break UX
      }
    };
    trackImpression();
  }, [triggerType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        name: name || undefined,
        interests: ['videos', 'tutorials']
      });
      
      // Track conversion
      try {
        await axios.post(`${API_URL}/api/popup/track?event_type=conversion&trigger_type=${triggerType}&page=video-gallery`);
      } catch (e) {
        // Silently fail
      }
      
      // Subscribe to email automation sequence
      try {
        await axios.post(`${API_URL}/api/emails/sequence/subscribe`, {
          email,
          name: name || undefined,
          sequence: 'video-welcome'
        });
      } catch (e) {
        // Silently fail - don't block the main flow
        console.log('Email sequence subscription:', e.message);
      }
      
      setSuccess(true);
      toast.success('Welcome! Check your email for exclusive video content.');
      
      // Auto-close after success
      setTimeout(() => {
        handleClose(true);
      }, 3000);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('You\'re already subscribed! Enjoy our video content.');
        handleClose(true);
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (subscribed = false) => {
    // Store dismissal in localStorage
    localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify({
      dismissed: true,
      subscribed,
      timestamp: Date.now()
    }));
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[200] flex items-center justify-center p-4"
      onClick={() => handleClose(false)}
    >
      <div 
        className="bg-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative h-48 bg-gradient-to-br from-[#003456] via-[#002845] to-[#001d30] overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#fdc31c] rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#fdc31c] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Play className="h-8 w-8 text-[#0a0203] ml-1" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                {isExitIntent ? "Wait! Before You Go..." : "Exclusive Video Content"}
              </h2>
            </div>
          </div>
          <button 
            onClick={() => handleClose(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            data-testid="close-email-popup"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!success ? (
            <>
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-[#0a0203] mb-2">
                  {isExitIntent ? "Don't Miss Out on Free Tutorials!" : "Get Free Video Tutorials"}
                </h3>
                <p className="text-gray-600">
                  {isExitIntent 
                    ? "Subscribe now and get instant access to exclusive freeze drying tutorials, pro tips, and recipes."
                    : "Subscribe to unlock exclusive freeze drying tutorials, pro tips, and recipes delivered straight to your inbox."
                  }
                </p>
              </div>

              {/* Benefits */}
              <div className="bg-[#f0f4f8] rounded-xl p-4 mb-6">
                <div className="flex items-start gap-3 mb-3">
                  <Gift className="h-5 w-5 text-[#003456] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0a0203] text-sm">Weekly Video Tips</p>
                    <p className="text-xs text-gray-500">New tutorials every week from freeze drying experts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-[#003456] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-[#0a0203] text-sm">Exclusive Content</p>
                    <p className="text-xs text-gray-500">Subscriber-only recipes and behind-the-scenes videos</p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full"
                  data-testid="email-popup-name"
                />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full"
                  data-testid="email-popup-email"
                />
                <Button 
                  type="submit" 
                  disabled={loading || !email}
                  className="w-full bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold py-6"
                  data-testid="email-popup-submit"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <div className="animate-spin h-4 w-4 border-2 border-[#0a0203] border-t-transparent rounded-full"></div>
                      Subscribing...
                    </span>
                  ) : (
                    'Get Free Video Access'
                  )}
                </Button>
              </form>

              <p className="text-xs text-gray-400 text-center mt-4">
                No spam, ever. Unsubscribe anytime.
              </p>
            </>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-[#0a0203] mb-2">
                You're In!
              </h3>
              <p className="text-gray-600 mb-4">
                Welcome to the HomeLyph community. Check your inbox for your first exclusive video!
              </p>
              <Button 
                onClick={() => handleClose(true)}
                className="bg-[#003456] hover:bg-[#002845]"
              >
                Start Watching Videos
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook to check if popup should be shown (with exit intent support)
export const useVideoEmailPopup = (options = {}) => {
  const { 
    enableExitIntent = true, 
    timedDelay = 3000,
    enableTimed = true 
  } = options;
  
  const [showPopup, setShowPopup] = useState(false);
  const [triggerType, setTriggerType] = useState(null); // 'timed' or 'exit-intent'
  const hasTriggeredRef = React.useRef(false);
  const exitIntentTriggeredThisSession = React.useRef(false);

  // Check if we should show popup at all
  const shouldShowPopup = () => {
    const stored = localStorage.getItem(POPUP_STORAGE_KEY);
    if (stored) {
      const data = JSON.parse(stored);
      // If already subscribed, never show again
      if (data.subscribed) return false;
      
      // If dismissed but not subscribed, show again after 7 days
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp < sevenDays) return false;
    }
    return true;
  };

  // Timed popup trigger
  useEffect(() => {
    if (!enableTimed || !shouldShowPopup()) return;

    const timer = setTimeout(() => {
      if (!hasTriggeredRef.current) {
        hasTriggeredRef.current = true;
        setTriggerType('timed');
        setShowPopup(true);
      }
    }, timedDelay);

    return () => clearTimeout(timer);
  }, [enableTimed, timedDelay]);

  // Exit intent detection
  useEffect(() => {
    if (!enableExitIntent || !shouldShowPopup()) return;

    const handleMouseLeave = (e) => {
      // Only trigger if mouse leaves from the top of the viewport
      if (e.clientY <= 0 && !hasTriggeredRef.current && !exitIntentTriggeredThisSession.current) {
        exitIntentTriggeredThisSession.current = true;
        hasTriggeredRef.current = true;
        setTriggerType('exit-intent');
        setShowPopup(true);
      }
    };

    // Add a small delay before enabling exit intent to avoid false triggers on page load
    const enableTimer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 1000);

    return () => {
      clearTimeout(enableTimer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enableExitIntent]);

  const closePopup = () => {
    setShowPopup(false);
    hasTriggeredRef.current = true; // Prevent re-showing on this page visit
  };

  return { showPopup, setShowPopup: closePopup, triggerType };
};

export default VideoEmailCapture;
